import { isoStringToDate } from "../shared/date-format";
import { ColumnDefinition, EntityInterface } from "./Table";

/**
 * A NLS Transaction represents a transaction that has been put into NLS
 * 
 * It contains information about the transaction including amount, loan details, buyer/supplier information,
 * and current status.
 */

/** NLS Transaction statuses */
export type NLSTransactionStatus = '0';

/**
 * Interface for a nls transaction
 */
export interface NLSTransactionInterface {
    transaction_id: number;              // Unique transaction identifier
    amount: number;                      // Transaction amount
    loan_number: string;                 // Associated loan number
    current_balance: number;             // Current balance on the loan
    buyer_company?: string;              // Buyer company name
    buyer_id?: string;                   // Buyer's company ID
    supplier_company?: string;           // Supplier company name
    supplier_id?: string;                // Supplier's company ID
    status: NLSTransactionStatus;        // Transaction status
    last_update_date: string | null;     // Date of last update (null if no updates)
    plan_id: string;                     // Plan ID associated with the transaction
    plan_desc: string;                   // Description of the plan
    plan_dur: number;                    // Duration of the plan in days
    plan_name: string;                   // Plan name
    plan_apr: string;                    // Plan apr
    due_date: string | null;             // Due date for the transaction
    origination_date?: string | null;    // Date the transaction was originated (optional)
    request_memo: string | null;         // Memo associated with the request
    request_file_ids?: number[];         // File IDs associated with the request (optional)
    next_payment_due: string;            // When the next paymet is due
    next_payment_total_amount: number;   // Amount due for next payment
}

/**
 * NLSTransaction class representing a nls transaction
 */
export class NLSTransaction implements EntityInterface {
    /**
     * Constructor for the NLSTransaction class
     * 
     * @param transaction_id Unique transaction identifier
     * @param amount Transaction amount
     * @param loan_number Associated loan number
     * @param current_balance Current balance on the loan
     * @param buyer_company Buyer company name
     * @param buyer_id Buyer's company ID
     * @param supplier_company Supplier company name
     * @param supplier_id Supplier's company ID
     * @param status Transaction status
     * @param last_update_date Date of last update (null if no updates)
     * @param plan_id Plan ID associated with the transaction (null if no plan)
     * @param plan_desc Description of the plan (null if no plan)
     * @param plan_dur Duration of the plan in days (null if no plan)
     * @param plan_name Plan name
     * @param plan_apr Plan apr
     * @param due_date Due date for the transaction
     * @param origination_date Date the transaction was originated (optional)
     * @param request_memo Memo associated with the request
     * @param request_file_ids File IDs associated with the request (optional)
     * @param next_payment_due When the next payment is due
     * @param next_payment_total_amount Amount due for next payment
     */
    constructor(
        public transaction_id: number,
        public amount: number,
        public loan_number: string,
        public current_balance: number,
        public buyer_company: string,
        public buyer_id: string,
        public supplier_company: string,
        public supplier_id: string,
        public status: NLSTransactionStatus,
        public last_update_date: Date | null,
        public plan_id: string,
        public plan_desc: string,
        public plan_dur: number,
        public plan_name: string,
        public plan_apr: string,
        public due_date: Date | null,
        public origination_date: Date | null,
        public request_memo: string | null,
        public request_file_ids: number[],
        public next_payment_due: string,
        public next_payment_total_amount: number,
    ) { }

    /**
     * Gets the ID for EntityInterface compliance
     */
    get id(): string {
        return this.transaction_id.toString();
    }

    /**
     * Creates a copy of the current NLSTransaction instance
     * 
     * @returns A copy of the current NLSTransaction instance
     */
    copyOf(): NLSTransaction {
        return new NLSTransaction(
            this.transaction_id,
            this.amount,
            this.loan_number,
            this.current_balance,
            this.buyer_company,
            this.buyer_id,
            this.supplier_company,
            this.supplier_id,
            this.status,
            this.last_update_date,
            this.plan_id,
            this.plan_desc,
            this.plan_dur,
            this.plan_name,
            this.plan_apr,
            this.due_date,
            this.origination_date,
            this.request_memo,
            this.request_file_ids,
            this.next_payment_due,
            this.next_payment_total_amount,
        );
    }

    /**
     * Return true if the current NLSTransaction instance is equal to the given transaction
     * 
     * @param other The other transaction to compare with
     * @returns True if the transactions are equal, false otherwise
     */
    equals(other: NLSTransaction): boolean {
        return this.transaction_id === other.transaction_id &&
            this.amount === other.amount &&
            this.loan_number === other.loan_number &&
            this.current_balance === other.current_balance &&
            this.buyer_company === other.buyer_company &&
            this.buyer_id === other.buyer_id &&
            this.supplier_company === other.supplier_company &&
            this.supplier_id === other.supplier_id &&
            this.status === other.status &&
            this.last_update_date === other.last_update_date &&
            this.plan_id === other.plan_id &&
            this.plan_desc === other.plan_desc &&
            this.plan_dur === other.plan_dur &&
            this.plan_name === other.plan_name &&
            this.plan_apr === other.plan_apr &&
            this.due_date === other.due_date &&
            this.origination_date === other.origination_date && 
            this.request_memo === other.request_memo &&
            JSON.stringify(this.request_file_ids) === JSON.stringify(other.request_file_ids) &&
            this.next_payment_due === other.next_payment_due &&
            this.next_payment_total_amount === other.next_payment_total_amount;
    }

    /**
     * Returns a string representation of the NLSTransaction instance for filtering
     * 
     * @param defs The column definitions to use for formatting
     * @returns A string representation of the NLSTransaction instance
     */
    toString(defs: ColumnDefinition<any>[]): string {
        let str = "";
        for (let col of defs) {
            if (col.toStringTransform != undefined) {
                str += col.toStringTransform((this as any)[col.field]) + "█";
            } else {
                str += (this as any)[col.field];
            }
        }

        return str.toLowerCase();
    }

    /**
     * Gets the status as a titlecase string
     * 
     * @returns The status in titlecase format
     */
    get statusTitleCase(): string {
        return this.status.charAt(0).toUpperCase() + this.status.slice(1);
    }

    /**
     * Gets the formatted amount as a currency string
     * 
     * @returns The amount formatted as currency
     */
    get formattedAmount(): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(this.amount);
    }

    /**
     * Gets the formatted current balance as a currency string
     * 
     * @returns The current balance formatted as currency
     */
    get formattedCurrentBalance(): string {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(this.current_balance);
    }

    /**
     * Gets the request memo with a default value if null
     * 
     * @returns The request memo or a default string if null
     */
    get displayRequestMemo(): string {
        return this.request_memo || "No memo provided";
    }
}

/** Mapper for converting between NLSTransactionInterface and NLSTransaction */
export class NLSTransactionMapper {
    /**
     * Converts a NLSTransaction model to a NLSTransactionInterface DTO
     * 
     * @param transaction The NLSTransaction model to convert
     * @returns The converted NLSTransactionInterface DTO
     */
    static toDto(transaction: NLSTransaction): NLSTransactionInterface {
        return {
            transaction_id: transaction.transaction_id,
            amount: transaction.amount,
            loan_number: transaction.loan_number,
            current_balance: transaction.current_balance,
            buyer_company: transaction.buyer_company,
            buyer_id: transaction.buyer_id,
            supplier_company: transaction.supplier_company,
            supplier_id: transaction.supplier_id,
            status: transaction.status,
            last_update_date: transaction.last_update_date?.toISOString() || null,
            plan_id: transaction.plan_id,
            plan_desc: transaction.plan_desc,
            plan_dur: transaction.plan_dur,
            plan_name: transaction.plan_name,
            plan_apr: transaction.plan_apr,
            due_date: transaction.due_date?.toISOString() || null,
            origination_date: transaction.origination_date?.toISOString() || null,
            request_memo: transaction.request_memo,
            request_file_ids: transaction.request_file_ids,
            next_payment_due: transaction.next_payment_due,
            next_payment_total_amount: transaction.next_payment_total_amount,
        };
    }

    /**
     * Converts a NLSTransactionInterface DTO to a NLSTransaction model
     * 
     * @param dto The NLSTransactionInterface DTO to convert
     * @returns The converted NLSTransaction model
     */
    static toModel(dto: NLSTransactionInterface): NLSTransaction {
        
        return new NLSTransaction(
            dto.transaction_id,
            dto.amount,
            dto.loan_number,
            dto.current_balance,
            dto.buyer_company || '',
            dto.buyer_id || '',
            dto.supplier_company || '',
            dto.supplier_id || '',
            dto.status,
            isoStringToDate(dto.last_update_date),
            dto.plan_id,
            dto.plan_desc,
            dto.plan_dur,
            dto.plan_name,
            dto.plan_apr,
            isoStringToDate(dto.due_date),
            isoStringToDate(dto.origination_date || null),
            dto.request_memo || null,
            dto.request_file_ids || [],
            dto.next_payment_due,
            dto.next_payment_total_amount,
        );
    }
}